import { z } from 'zod';
import { milliUnitsSchema, positiveMilliUnitsSchema, stockUnitSchema } from '../inventory/units.js';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema } from '../money/payment-methods.js';
import { gstinSchema } from '../money/gst.js';
import { stockCategorySchema } from './inventory.js';
import { indianMobileSchema, objectIdSchema } from './common.js';

/**
 * How material gets into stock (task 28) — the buying side of task 27's ledger.
 *
 * The rule that shapes everything here: THIS MODULE NEVER SETS A QUANTITY. A goods receipt
 * writes movements through task 27's `applyMovement`, exactly as a hand-recorded purchase does,
 * so there is still only one way a shelf can change and still only one place to look when a
 * number is wrong. Purchasing owns what was ORDERED and what is OWED; stock owns what is there.
 *
 * The second rule: WHAT ARRIVED IS NOT WHAT WAS ORDERED, and the difference is recorded rather
 * than reconciled away. Short deliveries, substituted colours and damaged rolls are the ordinary
 * case in this trade, and a receipt form that only accepted the ordered quantity would be a form
 * people work around instead of using.
 */

// --- Suppliers ---------------------------------------------------------------------------------

export const supplierSchema = z.object({
  id: z.string(),
  name: z.string(),
  contactPerson: z.string().nullable(),
  phone: z.string(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  gstin: z.string().nullable(),
  /** Which of task 27's categories they supply — what the "who sells lining?" question reads. */
  supplies: z.array(stockCategorySchema),
  paymentTerms: z.string().nullable(),
  notes: z.string().nullable(),
  active: z.boolean(),
  /** Received goods not yet paid for, less any advance already with them. */
  outstandingPaise: paiseSchema,
  unappliedAdvancePaise: paiseSchema,
  /** How much has ever been bought from them — the "are they worth keeping?" figure. */
  lifetimePurchasedPaise: paiseSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Supplier = z.infer<typeof supplierSchema>;

export const createSupplierSchema = z.object({
  name: z.string().trim().min(1).max(120),
  contactPerson: z.string().trim().max(120).optional(),
  phone: indianMobileSchema,
  email: z.string().trim().email().max(160).optional(),
  address: z.string().trim().max(300).optional(),
  gstin: gstinSchema.optional(),
  supplies: z.array(stockCategorySchema).min(1).max(8),
  paymentTerms: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(500).optional(),
});
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;

export const updateSupplierSchema = createSupplierSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

// --- Purchase orders ------------------------------------------------------------------------------

/**
 * Where a purchase order stands.
 *
 * `draft` exists because a shop builds a PO over a morning — adding to it as they notice what is
 * running low — and only then sends it. `partially_received` is the ordinary outcome, not the
 * exception: three of five rolls arrive on Tuesday and the rest on Friday.
 */
export const PO_STATUSES = [
  'draft',
  'sent',
  'partially_received',
  'received',
  'cancelled',
] as const;
export type PoStatus = (typeof PO_STATUSES)[number];
export const poStatusSchema = z.enum(PO_STATUSES);

export const poLineSchema = z.object({
  lineId: z.string(),
  /** Null for something the shop has not stocked before — created on first receipt. */
  stockItemId: z.string().nullable(),
  variantId: z.string().nullable(),
  description: z.string(),
  category: stockCategorySchema,
  unit: stockUnitSchema,
  /** Canonical milli-units, as everywhere in task 27. */
  orderedQuantity: milliUnitsSchema,
  receivedQuantity: milliUnitsSchema,
  ratePaise: nonNegativePaiseSchema,
  amountPaise: paiseSchema,
  /** ordered − received, once the line is closed. Positive means short. */
  shortQuantity: milliUnitsSchema,
});
export type PoLine = z.infer<typeof poLineSchema>;

export const purchaseOrderSchema = z.object({
  id: z.string(),
  number: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  status: poStatusSchema,
  lines: z.array(poLineSchema),
  expectedOn: z.string().nullable(),
  sentAt: z.string().nullable(),
  notes: z.string().nullable(),
  totalPaise: paiseSchema,
  /** What has actually arrived, at the rates it arrived at. */
  receivedPaise: paiseSchema,
  /** True when it is sent, not fully received, and the expected date has passed. */
  overdue: z.boolean(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

export const poLineInputSchema = z.object({
  stockItemId: objectIdSchema.optional(),
  variantId: z.string().optional(),
  /** Required only when the line is not an existing stock item. */
  description: z.string().trim().max(160).optional(),
  category: stockCategorySchema.optional(),
  unit: stockUnitSchema.optional(),
  quantity: positiveMilliUnitsSchema,
  ratePaise: nonNegativePaiseSchema,
});
export type PoLineInput = z.infer<typeof poLineInputSchema>;

export const createPurchaseOrderSchema = z.object({
  supplierId: objectIdSchema,
  lines: z.array(poLineInputSchema).min(1).max(100),
  expectedOn: z.union([z.date(), z.string()]).optional(),
  notes: z.string().trim().max(500).optional(),
});
export type CreatePurchaseOrderInput = z.infer<typeof createPurchaseOrderSchema>;

/**
 * Goods arriving.
 *
 * Every line names what ACTUALLY came — quantity and rate — because both routinely differ from
 * what was ordered, and a receipt that assumed otherwise would quietly file the wrong numbers
 * into the stock ledger and the payable at once.
 */
export const receiveGoodsSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().min(1),
        quantity: positiveMilliUnitsSchema,
        /** What was actually charged. Left out, the ordered rate stands. */
        ratePaise: nonNegativePaiseSchema.optional(),
        /** A substituted colour, a torn roll — recorded, never reconciled away. */
        discrepancy: z.string().trim().max(300).optional(),
      }),
    )
    .min(1)
    .max(100),
  receivedOn: z.union([z.date(), z.string()]).optional(),
  supplierInvoiceNumber: z.string().trim().max(60).optional(),
  billMediaIds: z.array(objectIdSchema).max(10).default([]),
  /** Closes the order even though something is short — the rest is never coming. */
  closeShort: z.boolean().default(false),
  note: z.string().trim().max(300).optional(),
  /**
   * A key the client mints once per delivery it is recording and re-sends on a retry. A second
   * request with the same key answers with the receipt the first one wrote instead of moving the
   * stock again. Optional: a receipt without one is simply not retry-safe beyond the ordered
   * quantity cap.
   */
  requestId: z.string().trim().min(8).max(100).optional(),
});
export type ReceiveGoodsInput = z.infer<typeof receiveGoodsSchema>;

export const goodsReceiptSchema = z.object({
  id: z.string(),
  number: z.string(),
  purchaseOrderId: z.string(),
  purchaseOrderNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  receivedOn: z.string(),
  supplierInvoiceNumber: z.string().nullable(),
  billMediaIds: z.array(z.string()),
  lines: z.array(
    z.object({
      lineId: z.string(),
      description: z.string(),
      quantity: milliUnitsSchema,
      ratePaise: nonNegativePaiseSchema,
      amountPaise: paiseSchema,
      discrepancy: z.string().nullable(),
    }),
  ),
  totalPaise: paiseSchema,
  paidAt: z.string().nullable(),
  note: z.string().nullable(),
  recordedByName: z.string().nullable(),
});
export type GoodsReceipt = z.infer<typeof goodsReceiptSchema>;

// --- Paying the supplier -----------------------------------------------------------------------------

export const recordSupplierAdvanceSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema,
  occurredAt: z.union([z.date(), z.string()]).optional(),
  note: z.string().trim().max(300).optional(),
});
export type RecordSupplierAdvanceInput = z.infer<typeof recordSupplierAdvanceSchema>;

export const paySupplierSchema = z.object({
  receiptIds: z.array(objectIdSchema).min(1).max(200),
  method: paymentMethodSchema,
  paidAt: z.union([z.date(), z.string()]).optional(),
  note: z.string().trim().max(300).optional(),
});
export type PaySupplierInput = z.infer<typeof paySupplierSchema>;

export const supplierPaymentSchema = z.object({
  id: z.string(),
  number: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  paidAt: z.string(),
  method: paymentMethodSchema,
  goodsPaise: paiseSchema,
  advanceAppliedPaise: paiseSchema,
  netPaise: paiseSchema,
  receiptIds: z.array(z.string()),
  expenseId: z.string().nullable(),
  note: z.string().nullable(),
  recordedByName: z.string().nullable(),
});
export type SupplierPayment = z.infer<typeof supplierPaymentSchema>;

// --- Views ---------------------------------------------------------------------------------------------

export const supplierStatementSchema = z.object({
  supplier: supplierSchema,
  /** Receipts that have arrived and not been paid for. */
  unpaidReceipts: z.array(goodsReceiptSchema),
  unpaidPaise: paiseSchema,
  unappliedAdvancePaise: paiseSchema,
  netPayablePaise: paiseSchema,
  openOrders: z.array(purchaseOrderSchema),
  payments: z.array(supplierPaymentSchema),
});
export type SupplierStatement = z.infer<typeof supplierStatementSchema>;

/** The rate a shop paid for one item over time — how you spot a supplier creeping up. */
export const purchaseHistoryPointSchema = z.object({
  receivedOn: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  quantity: milliUnitsSchema,
  ratePaise: nonNegativePaiseSchema,
  purchaseOrderNumber: z.string(),
});
export type PurchaseHistoryPoint = z.infer<typeof purchaseHistoryPointSchema>;

export const purchaseHistorySchema = z.object({
  stockItemId: z.string(),
  itemName: z.string(),
  unit: stockUnitSchema,
  points: z.array(purchaseHistoryPointSchema),
  /** First and last rate in the series, so "up 12% since April" is one subtraction away. */
  firstRatePaise: nonNegativePaiseSchema.nullable(),
  lastRatePaise: nonNegativePaiseSchema.nullable(),
});
export type PurchaseHistory = z.infer<typeof purchaseHistorySchema>;

export const reorderSuggestionSchema = z.object({
  stockItemId: z.string(),
  itemName: z.string(),
  category: stockCategorySchema,
  unit: stockUnitSchema,
  quantity: milliUnitsSchema,
  reorderLevel: milliUnitsSchema,
  /** Enough to reach twice the reorder level — a working rule, shown as a suggestion. */
  suggestedQuantity: positiveMilliUnitsSchema,
  lastRatePaise: nonNegativePaiseSchema,
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  /** True when this item is already on an open purchase order. */
  alreadyOnOrder: z.boolean(),
});
export type ReorderSuggestion = z.infer<typeof reorderSuggestionSchema>;

export const reorderSuggestionsSchema = z.object({
  items: z.array(reorderSuggestionSchema),
  estimatedCostPaise: paiseSchema,
});
export type ReorderSuggestions = z.infer<typeof reorderSuggestionsSchema>;
